import { ISubmitProps } from '../../../containers/FormContainer/types/internal';
import { getMapper } from './answerFieldMappers';
import { TMappedAnswersState } from '../types';
import { EAnswerField } from '../../../types/form';

/**
 * Эти поля не будут явно смаплены из answersState
 * см.2й этап внутри mapAnswersState
 * */
const RESTRICTED_MAPPING_ANSWER_FIELDS: EAnswerField[] = [EAnswerField.Address];

/**
 * Утилита мапит стейт формы в формат для передачи в API
 * Данные собираются на основе пройденных юзером шагов + предзаполненных шагов
 * */
export const mapAnswersState = (props: ISubmitProps): TMappedAnswersState => {
  let mappedAnswersState = {} as TMappedAnswersState;

  /**
   * Сначала мапим ответы юзера, на которые он физически ответил + которые были "пропущены" за счет skipSteps
   * "Пропустить шаг" - значит пройти путь: предыдущий -> пропускаемый (не увидит) -> следующий
   * Если юзер не "пропустил" шаг, который находится в skipSteps (например шаг может быть в другой ветке) - то
   * этот шаг не окажется в stepsHistory. Если шаг не в stepsHistory - то он не смапится.
   * */
  mappedAnswersState = props.stepsHistory.reduce((acc, step) => {
    const stepCode = step.stepCode;
    const questionCode = props.stepsMap[stepCode].questionCode;
    const question = props.questionsMap[questionCode];
    const answer = props.answersState[question.answerField];

    if (!answer) {
      return acc;
    }

    const mapper = getMapper(question.type, question.answerType, question.answerField);

    return {
      ...acc,
      ...mapper(answer),
    };
  }, mappedAnswersState);

  /**
   * Затем мапятся остальные ответы из answersState, которые не были смаплены на 1м этапе
   * Такое может произойти, например, когда начальный стейт формы наполнился ответами из URL или из userData
   * Т.к начальный стейт не попадает в stepsHistory, он не будет мапиться на 1м этапе
   * */
  Object.keys(props.answersState).forEach((answerField: keyof typeof props.answersState) => {
    const answer = props.answersState[answerField];

    /** Если поле из answersState уже смапилось или если answer пуст - ничего не делаем */
    if (answerField in mappedAnswersState || !answer) {
      return;
    }

    /**
     * Попытка найти вопрос, у которого answerField совпадает с answerField из answersState
     * Делается с целью валидации. Если вдруг в стейте ответов оказалось что то левое, оно не должно смапиться
     * Если такого вопроса нет - ничего не делаем
     * */
    const question = Object.values(props.questionsMap).find(q => q.answerField === answerField);

    if (!question) {
      return;
    }

    /** Если answerField в списке полей, которые не нужно мапить - ничего не делаем */
    if (RESTRICTED_MAPPING_ANSWER_FIELDS.includes(answerField)) {
      return;
    }

    const mapper = getMapper(question.type, question.answerType, answerField);
    const mappedAnswer = mapper(answer);

    Object.assign(mappedAnswersState, mappedAnswer);
  });

  return mappedAnswersState;
};
